import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { StudioXCredit } from './StudioXCredit';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  ClipboardList,
  BookOpen,
  Shield,
  Building2,
  Users,
  LogOut,
  Menu,
  X,
  User,
  UserCog,
  ChevronUp,
  ChevronDown,
  Target,
} from 'lucide-react';

export function MainLayout() {
  const { user, profile, signOut, isSuperAdmin, isEditor, isOrgAdmin, isDeptAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [approachesOpen, setApproachesOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch all published approaches when user is available
  const [approaches, setApproaches] = useState<any[] | null>(null);
  useEffect(() => {
    if (!user) return;
    supabase.from('approaches').select('*').eq('status', 'published').order('number')
      .then(({ data }) => setApproaches(data));
  }, [user]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    localStorage.clear();
    sessionStorage.clear();
    // Redirect regardless of signOut outcome after 2 seconds
    const fallback = setTimeout(() => { window.location.href = '/login'; }, 2000);
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
    clearTimeout(fallback);
    window.location.href = '/login';
  };

  const isActive = (href: string) => {
    if (href === '/assessment/start') {
      return location.pathname.startsWith('/assessment');
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // If user is not logged in, show only the content without sidebar
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Outlet />
        <StudioXCredit />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300
        fixed inset-y-0 left-0 z-50 md:relative md:z-auto md:translate-x-0
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${sidebarOpen ? 'w-64' : 'w-64 md:w-16'}
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Link to="/" className={`flex items-center ${!sidebarOpen && 'justify-center'}`}>
              {sidebarOpen ? (
                <>
                  <img 
                    src="/logo_light.png" 
                    alt="Helsenøkkelen" 
                    className="h-8 w-auto dark:hidden"
                  />
                  <img 
                    src="/logo_dark.png" 
                    alt="Helsenøkkelen" 
                    className="h-8 w-auto hidden dark:block"
                  />
                </>
              ) : (
                <Target className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              )}
            </Link>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Ny vurdering */}
          <Link
            to="/assessment/start"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center space-x-3 px-3 py-2 rounded-md transition-colors ${
              isActive('/assessment/start')
                ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            } ${!sidebarOpen && 'justify-center'}`}
            title={!sidebarOpen ? 'Ny vurdering' : undefined}
          >
            <ClipboardList className="h-5 w-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Ny vurdering</span>}
          </Link>

          {/* Tilnærminger dropdown */}
          {sidebarOpen ? (
            <div>
              <button
                onClick={() => setApproachesOpen(!approachesOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors ${
                  location.pathname.startsWith('/approaches')
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <BookOpen className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">Tilnærminger</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${approachesOpen ? 'rotate-180' : ''}`} />
              </button>
              {approachesOpen && approaches && (
                <div className="mt-1 ml-4 space-y-1">
                  {approaches.map((approach) => (
                    <Link
                      key={approach.id}
                      to={`/approaches/${approach.number}`}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                        location.pathname === `/approaches/${approach.number}`
                          ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {approach.name_no}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/approaches/1"
              className={`flex items-center justify-center px-3 py-2 rounded-md transition-colors ${
                location.pathname.startsWith('/approaches')
                  ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title="Tilnærminger"
            >
              <BookOpen className="h-5 w-5 flex-shrink-0" />
            </Link>
          )}

          {/* Divider */}
          {(isSuperAdmin || isEditor || isOrgAdmin || isDeptAdmin) && (
            <div className="my-4 border-t border-gray-200 dark:border-gray-700" />
          )}

          {/* Superadmin / Editor link */}
          {(isSuperAdmin || isEditor) && (
            <Link
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center space-x-3 px-3 py-2 rounded-md transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ${!sidebarOpen && 'justify-center'}`}
              title={!sidebarOpen ? (isSuperAdmin ? 'Superadmin' : 'Admin') : undefined}
            >
              <Shield className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">{isSuperAdmin ? 'Superadmin' : 'Admin'}</span>}
            </Link>
          )}

          {/* Company Admin link */}
          {isOrgAdmin && (
            <Link
              to="/company-admin"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center space-x-3 px-3 py-2 rounded-md transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ${!sidebarOpen && 'justify-center'}`}
              title={!sidebarOpen ? 'Bedriftsadmin' : undefined}
            >
              <Building2 className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Bedriftsadmin</span>}
            </Link>
          )}

          {/* Department Admin link */}
          {isDeptAdmin && (
            <Link
              to="/dept-admin"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center space-x-3 px-3 py-2 rounded-md transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ${!sidebarOpen && 'justify-center'}`}
              title={!sidebarOpen ? 'Min avdeling' : undefined}
            >
              <Users className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Min avdeling</span>}
            </Link>
          )}
        </nav>

        {/* User section */}
        <div ref={userMenuRef} className="p-4 border-t border-gray-200 dark:border-gray-700 relative">
          {/* User Menu Dropdown */}
          {userMenuOpen && sidebarOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-2 mx-4 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 overflow-hidden z-50">
              {/* Profile link */}
              <Link
                to="/profile"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">Rediger profil</span>
              </Link>
              {/* Profile information */}
              <div className="px-4 py-3 space-y-2 border-t border-gray-100 dark:border-gray-600">
                <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                  <User className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{profile?.fullName || 'Ikke angitt'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <svg className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm truncate">{user?.email || 'Ikke angitt'}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <UserCog className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm font-medium capitalize">{profile?.role?.replace('_', ' ') || 'Bruker'}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className={`flex items-center gap-2 ${sidebarOpen ? '' : 'justify-center flex-col'}`}>
            {sidebarOpen && user && (
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex-1 flex items-center gap-2 p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {profile?.fullName || user?.email || 'User'}
                  </p>
                  {profile?.role && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">{profile.role.replace('_', ' ')}</p>
                  )}
                </div>
                <ChevronUp className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            )}
            {user && (
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logg ut"
              >
                <LogOut className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src="/logo_light.png" alt="Helsenøkkelen" className="h-7 w-auto dark:hidden" />
          <img src="/logo_dark.png" alt="Helsenøkkelen" className="h-7 w-auto hidden dark:block" />
        </div>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
          <StudioXCredit />
        </main>
      </div>
    </div>
  );
}

